import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CaretIcon, DropDownOption } from '@grays-npm/component-library';

type NativeDropDownProps = {
  initialValue?: string;
  onChange: (option: string) => void;
  options: DropDownOption[];
};

const Wrapper = styled.div<{ selectWidth: number }>`
  position: relative;
  width: ${({ selectWidth }) => selectWidth}px;
  max-width: 100%;
  svg {
    transform: rotate(0deg);
    transition: transform 200ms ease-in-out;
  }
  &:focus-within svg {
    transform: rotate(180deg);
  }
`;

const Select = styled.select`
  ${({ theme }) => theme.typography.uiText.md}
  appearance: none;
  background-color: ${({ theme }) => theme.colors.primary.white};
  border: none;
  color: ${({ theme }) => theme.colors.system.systemBlue};
  padding: ${({ theme }) =>
    `${theme.spacings[4].rem} ${theme.spacings[6].rem} ${theme.spacings[2].rem} ${theme.spacings[3].rem}`};
  max-width: 100%;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
  }
`;

const Icon = styled(CaretIcon)`
  fill: ${({ theme }) => theme.colors.system.systemBlue};
  height: 100%;
  pointer-events: none;
  position: absolute;
  right: ${({ theme }) => theme.spacings[3].rem};
  top: 4px;
  width: 14px;
`;

const SelectedOption = styled.div`
  color: ${({ theme }) => theme.colors.primary.white};
  height: 0;
`;

export const NativeDropDown: React.FC<NativeDropDownProps> = ({
  initialValue,
  onChange,
  options,
}) => {
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(
    options.find((opt) => opt.value === initialValue)?.label ?? options[0].label
  );
  const [width, setWidth] = useState(0);
  const selectRef = useRef(null);

  useEffect(() => {
    const selectWidth =
      (selectRef.current as unknown as HTMLElement)?.offsetWidth + 44;
    setWidth(selectWidth);
  }, [selectedOptionLabel]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOptionLabel(event.target.selectedOptions[0].innerText);
    onChange(event.target.value);
  };

  return (
    <>
      <Wrapper selectWidth={width}>
        <Select onChange={handleChange}>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Icon />
      </Wrapper>
      <SelectedOption>
        <span ref={selectRef}>{selectedOptionLabel}</span>
      </SelectedOption>
    </>
  );
};
